import {
  type AriaAttributes,
  type ComponentProps,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { appThemeApiRef, useApi } from '@backstage/frontend-plugin-api';
import {
  TdsBodyCell,
  TdsButton,
  TdsCard,
  TdsHeaderCell,
  TdsIcon,
  TdsMessage,
  TdsSpinner,
  TdsTable,
  TdsTableBody,
  TdsTableBodyRow,
  TdsTableHeader,
} from '@scania/tegel-react';
import { useNavigate } from 'react-router-dom';

type TegelModeProviderProps = {
  children: ReactNode;
};

type TegelPageProps = {
  children: ReactNode;
  className?: string;
};

type TegelPageHeaderProps = {
  actions?: ReactNode;
  className?: string;
  eyebrow?: string;
  subtitle?: ReactNode;
  title: ReactNode;
};

type TegelPageSectionProps = {
  actions?: ReactNode;
  bodyClassName?: string;
  children: ReactNode;
  className?: string;
  title: string;
};

type TegelActionListItem = {
  description?: ReactNode;
  href?: string;
  onClick?: () => void;
  title: ReactNode;
};

type TegelActionListProps = {
  ariaLabel?: string;
  className?: string;
  items: TegelActionListItem[];
};

type TegelEmptyStateProps = {
  action?: ReactNode;
  className?: string;
  description?: ReactNode;
  icon?: ReactNode;
  title: ReactNode;
};

type TegelGettingStartedSection = {
  body: ReactNode;
  id: string;
  navLabel?: string;
  title: string;
};

type TegelGettingStartedProps = {
  ariaLabel: string;
  lead: ReactNode;
  sections: TegelGettingStartedSection[];
};

type TegelInfoTile = {
  description: ReactNode;
  title: ReactNode;
};

type TegelInfoTilesProps = {
  items: TegelInfoTile[];
};

type TegelProcessStep = {
  description: ReactNode;
  title: ReactNode;
};

type TegelProcessStepsProps = {
  items: TegelProcessStep[];
};

type TegelPreviewPanelProps = {
  eyebrow?: string;
  items: string[];
  title: string;
};

type TegelIconName = NonNullable<ComponentProps<typeof TdsIcon>['name']>;

type TegelButtonBaseProps = ComponentProps<'button'>;

type TegelIconButtonProps = {
  ariaLabel: string;
  className?: string;
  controls?: string;
  disabled?: boolean;
  expanded?: boolean;
  hasPopup?: AriaAttributes['aria-haspopup'];
  icon: TegelIconName;
  iconSize?: string;
  iconTitle?: string;
  onClick?: ComponentProps<'button'>['onClick'];
  pressed?: boolean;
  type?: ComponentProps<'button'>['type'];
};

type TegelLoadingStateProps = {
  className?: string;
  description?: ReactNode;
  label?: string;
  size?: ComponentProps<typeof TdsSpinner>['size'];
};

type TegelErrorStateProps = {
  className?: string;
  description?: ReactNode;
  error?: unknown;
  title?: string;
};

type TegelTabButtonProps = Omit<
  TegelButtonBaseProps,
  'aria-selected' | 'role' | 'type'
> & {
  selected: boolean;
};

type TegelPillButtonProps = {
  className?: string;
  count?: ReactNode;
  label: ReactNode;
  onClick?: ComponentProps<'button'>['onClick'];
  selected?: boolean;
};

export const TegelButtonBase = ({
  className,
  type = 'button',
  ...props
}: TegelButtonBaseProps) => (
  <button
    className={['tegel-button-base', className].filter(Boolean).join(' ')}
    type={type}
    {...props}
  />
);

export const TegelTabButton = ({
  className,
  selected,
  ...props
}: TegelTabButtonProps) => (
  <TegelButtonBase
    {...props}
    aria-selected={selected}
    className={['tegel-tab-button', className, selected ? 'is-active' : undefined]
      .filter(Boolean)
      .join(' ')}
    role="tab"
  />
);

export const TegelPillButton = ({
  className,
  count,
  label,
  onClick,
  selected = false,
}: TegelPillButtonProps) => (
  <TegelButtonBase
    aria-pressed={selected}
    className={[className, selected ? 'is-selected' : undefined]
      .filter(Boolean)
      .join(' ')}
    onClick={onClick}
  >
    <span>{label}</span>
    {count !== undefined && <strong>{count}</strong>}
  </TegelButtonBase>
);

export const TegelPage = ({ children, className }: TegelPageProps) => (
  <main className={['tegel-page', className].filter(Boolean).join(' ')}>
    {children}
  </main>
);

export const TegelPageHeader = ({
  actions,
  className,
  eyebrow,
  subtitle,
  title,
}: TegelPageHeaderProps) => (
  <header
    className={['tegel-page-header', className].filter(Boolean).join(' ')}
  >
    <div>
      {eyebrow && <p className="tegel-page-header-eyebrow">{eyebrow}</p>}
      <h1>{title}</h1>
      {subtitle && <p>{subtitle}</p>}
    </div>
    {actions && <div className="tegel-page-header-actions">{actions}</div>}
  </header>
);

export const TegelPageSection = ({
  actions,
  bodyClassName,
  children,
  className,
  title,
}: TegelPageSectionProps) => (
  <section
    className={['tegel-page-section', className].filter(Boolean).join(' ')}
  >
    <TegelCard
      title={title}
      actions={actions}
      bodyClassName={['tegel-page-section-body', bodyClassName]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </TegelCard>
  </section>
);

export const TegelActionList = ({
  ariaLabel,
  className,
  items,
}: TegelActionListProps) => (
  <ul
    aria-label={ariaLabel}
    className={['tegel-action-list', className].filter(Boolean).join(' ')}
  >
    {items.map(item => {
      const content = (
        <>
          <span>
            <strong>{item.title}</strong>
            {item.description && <small>{item.description}</small>}
          </span>
          <TdsIcon name="arrow_right" size="18px" svgTitle="Open" />
        </>
      );

      return (
        <li key={String(item.title)}>
          {item.href ? (
            <a className="tegel-action-list-link" href={item.href}>
              {content}
            </a>
          ) : (
            <TegelButtonBase
              className="tegel-action-list-button"
              onClick={item.onClick}
            >
              {content}
            </TegelButtonBase>
          )}
        </li>
      );
    })}
  </ul>
);

export const TegelEmptyState = ({
  action,
  className,
  description,
  icon,
  title,
}: TegelEmptyStateProps) => (
  <div
    className={['tegel-empty-state', className].filter(Boolean).join(' ')}
    role="status"
  >
    {icon && <div className="tegel-empty-state-icon">{icon}</div>}
    <strong>{title}</strong>
    {description && <p>{description}</p>}
    {action && <div className="tegel-empty-state-action">{action}</div>}
  </div>
);

export const TegelIconButton = ({
  ariaLabel,
  className,
  controls,
  disabled,
  expanded,
  hasPopup,
  icon,
  iconSize = '20px',
  iconTitle,
  onClick,
  pressed,
  type = 'button',
}: TegelIconButtonProps) => (
  <TegelButtonBase
    aria-controls={controls}
    aria-expanded={expanded}
    aria-haspopup={hasPopup}
    aria-label={ariaLabel}
    aria-pressed={pressed}
    className={['tegel-icon-button', className].filter(Boolean).join(' ')}
    disabled={disabled}
    onClick={onClick}
    title={ariaLabel}
    type={type}
  >
    <TdsIcon name={icon} size={iconSize} svgTitle={iconTitle ?? ariaLabel} />
  </TegelButtonBase>
);

export const TegelLoadingState = ({
  className,
  description,
  label = 'Loading',
  size = 'md',
}: TegelLoadingStateProps) => (
  <div
    aria-live="polite"
    className={['tegel-loading-state', className].filter(Boolean).join(' ')}
    role="status"
  >
    <TdsSpinner size={size} />
    <div>
      <strong>{label}</strong>
      {description && <p>{description}</p>}
    </div>
  </div>
);

type TegelNoticeProps = {
  children: ReactNode;
  className?: string;
  header?: string;
  variant?: 'information' | 'error' | 'warning' | 'success';
};

export const TegelNotice = ({
  children,
  className,
  header,
  variant = 'information',
}: TegelNoticeProps) => (
  <div className={['tegel-notice', className].filter(Boolean).join(' ')}>
    <TdsMessage
      header={header}
      modeVariant="secondary"
      tdsAriaLabel={header ?? `${variant} message`}
      variant={variant}
    >
      <div className="tegel-notice-body">{children}</div>
    </TdsMessage>
  </div>
);

const getErrorMessage = (error: unknown) => {
  if (!error) {
    return undefined;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return undefined;
};

export const TegelErrorState = ({
  className,
  description,
  error,
  title = 'Something went wrong',
}: TegelErrorStateProps) => {
  const message = getErrorMessage(error);

  return (
    <TegelNotice
      className={['tegel-error-state', className].filter(Boolean).join(' ')}
      header={title}
      variant="error"
    >
      {description && <p>{description}</p>}
      {message && <p>{message}</p>}
      {!description && !message && (
        <p>Please try again or contact support if the problem continues.</p>
      )}
    </TegelNotice>
  );
};

export const TegelGettingStarted = ({
  ariaLabel,
  lead,
  sections,
}: TegelGettingStartedProps) => {
  const [activeSectionId, setActiveSectionId] = useState(sections[0]?.id ?? '');

  useEffect(() => {
    setActiveSectionId(current =>
      sections.some(section => section.id === current)
        ? current
        : (sections[0]?.id ?? ''),
    );
  }, [sections]);

  useEffect(() => {
    if (!sections.length) {
      return undefined;
    }

    let frame = 0;

    const syncActiveSection = () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }

      frame = requestAnimationFrame(() => {
        const sectionElements = sections
          .map(section => document.getElementById(section.id))
          .filter((element): element is HTMLElement => Boolean(element));

        if (!sectionElements.length) {
          return;
        }

        const activationOffset = 140;
        const activeElement = sectionElements.reduce((current, element) => {
          return element.getBoundingClientRect().top <= activationOffset
            ? element
            : current;
        }, sectionElements[0]);

        setActiveSectionId(activeElement.id);
      });
    };

    syncActiveSection();
    window.addEventListener('scroll', syncActiveSection, { passive: true });
    window.addEventListener('resize', syncActiveSection);

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', syncActiveSection);
      window.removeEventListener('resize', syncActiveSection);
    };
  }, [sections]);

  return (
    <section
      id="getting-started"
      className="tegel-getting-started"
      aria-label={ariaLabel}
    >
      <div className="tegel-getting-started-grid">
        <div className="tegel-getting-started-copy">
          <p className="tegel-getting-started-lead">{lead}</p>

          {sections.map(section => (
            <section id={section.id} key={section.id}>
              <h2>{section.title}</h2>
              <div className="tegel-getting-started-section-body">
                {section.body}
              </div>
            </section>
          ))}
        </div>

        <nav
          className="tegel-getting-started-nav"
          aria-label={`${ariaLabel} sections`}
        >
          {sections.map(section => {
            const isActive = activeSectionId === section.id;

            return (
              <a
                aria-current={isActive ? 'location' : undefined}
                className={isActive ? 'is-active' : undefined}
                href={`#${section.id}`}
                key={section.id}
                onClick={() => setActiveSectionId(section.id)}
              >
                {section.navLabel ?? section.title}
              </a>
            );
          })}
        </nav>
      </div>
    </section>
  );
};

export const TegelInfoTiles = ({ items }: TegelInfoTilesProps) => (
  <div className="tegel-info-tiles">
    {items.map(item => (
      <article className="tegel-info-tile" key={String(item.title)}>
        <h3>{item.title}</h3>
        <p>{item.description}</p>
      </article>
    ))}
  </div>
);

export const TegelProcessSteps = ({ items }: TegelProcessStepsProps) => (
  <ol className="tegel-process-steps">
    {items.map((item, index) => (
      <li key={String(item.title)}>
        <span aria-hidden="true">{index + 1}</span>
        <div>
          <strong>{item.title}</strong>
          <p>{item.description}</p>
        </div>
      </li>
    ))}
  </ol>
);

export const TegelPreviewPanel = ({
  eyebrow,
  items,
  title,
}: TegelPreviewPanelProps) => (
  <aside className="tegel-preview-panel" aria-label={title}>
    {eyebrow && <span>{eyebrow}</span>}
    <strong>{title}</strong>
    <div>
      {items.map(item => (
        <small key={item}>{item}</small>
      ))}
    </div>
  </aside>
);

export const TegelModeProvider = ({ children }: TegelModeProviderProps) => {
  const appThemeApi = useApi(appThemeApiRef);
  const [activeThemeId, setActiveThemeId] = useState(
    appThemeApi.getActiveThemeId(),
  );
  const modeClass =
    activeThemeId === 'darkTheme' ? 'tds-mode-dark' : 'tds-mode-light';

  useEffect(() => {
    const subscription = appThemeApi
      .activeThemeId$()
      .subscribe(themeId => setActiveThemeId(themeId));

    return () => subscription.unsubscribe();
  }, [appThemeApi]);

  useEffect(() => {
    document.body.classList.remove('tds-mode-light', 'tds-mode-dark');
    document.body.classList.add(modeClass);

    return () => {
      document.body.classList.remove(modeClass);
    };
  }, [modeClass]);

  return <div className={modeClass}>{children}</div>;
};

type TegelCardProps = {
  actions?: ReactNode;
  bodyClassName?: string;
  cardId?: string;
  children: ReactNode;
  className?: string;
  modeVariant?: 'primary' | 'secondary';
  stretch?: boolean;
  subheader?: string;
  title: string;
};

const safeCardId = (title: string, cardId?: string) =>
  cardId ??
  `tegel-card-${title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')}`;

export function TegelCard({
  actions,
  bodyClassName,
  cardId,
  children,
  className,
  modeVariant,
  stretch = true,
  subheader,
  title,
}: TegelCardProps) {
  return (
    <TdsCard
      cardId={safeCardId(title, cardId)}
      className={['tegel-card', className].filter(Boolean).join(' ')}
      modeVariant={modeVariant}
      stretch={stretch}
    >
      <div slot="header" className="tegel-card-title">
        {title}
      </div>
      {subheader && (
        <div slot="subheader" className="tegel-card-subheader">
          {subheader}
        </div>
      )}
      <div
        slot="body"
        className={['tegel-card-body', bodyClassName].filter(Boolean).join(' ')}
      >
        {children}
      </div>
      {actions && (
        <div slot="actions" className="tegel-card-actions">
          {actions}
        </div>
      )}
    </TdsCard>
  );
}

const supportLinks = [
  {
    href: 'https://scaniaazureservices.sharepoint.com/sites/EU_SE_IT_C0010/SitePages/Page(8).aspx',
    label: 'Driveline Documentation',
    text: 'Complete guide and resources',
  },
  {
    href: 'https://scaniaazureservices.sharepoint.com/sites/EU_SE_IT_C0010/SitePages/FAQ-Template.aspx?ct=1753107420969&or=Teams-HL&ga=1&LOF=1',
    label: 'FAQs',
    text: 'Frequently asked questions',
  },
  {
    href: 'https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=5GLAO52sF0y03TqtY3_xrFabN6D8p-pFokTPMDLpQtFUN1dYWUNOVzNURUZYSERCWUFRUVhLM1FRWS4u',
    label: 'Support team',
    text: 'Contact us',
  },
];

type TegelSupportMenuProps = {
  includeFeedback?: boolean;
};

export const TegelSupportMenu = ({
  includeFeedback = true,
}: TegelSupportMenuProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const links = includeFeedback ? supportLinks : supportLinks.slice(0, 2);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  return (
    <div className="tegel-support-menu" ref={rootRef}>
      <TdsButton
        text="Support"
        variant="secondary"
        size="md"
        tdsAriaLabel="Support and help"
        onClick={() => setOpen(current => !current)}
      />
      {open && (
        <div className="tegel-support-popover">
          <TegelCard title="Support & Help" cardId="tegel-support-menu">
            <ul className="tegel-support-list">
              {links.map(link => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                  >
                    <strong>{link.label}</strong>
                    <span>{link.text}</span>
                  </a>
                </li>
              ))}
            </ul>
          </TegelCard>
        </div>
      )}
    </div>
  );
};

type TegelRouteButtonProps = {
  to: string;
  text: string;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
};

export const TegelRouteButton = ({
  text,
  to,
  variant = 'primary',
}: TegelRouteButtonProps) => {
  const navigate = useNavigate();

  return (
    <TdsButton
      text={text}
      variant={variant}
      size="md"
      onClick={() => navigate(to)}
    />
  );
};

export type TegelTableColumn<T> = {
  align?: 'left' | 'start' | 'right' | 'end' | 'center';
  className?: string;
  header: string;
  key: string;
  render: (row: T) => ReactNode;
  width?: string;
};

type TegelTableProps<T> = {
  className?: string;
  columns: Array<TegelTableColumn<T>>;
  compact?: boolean;
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  rowAriaLabel?: (row: T) => string;
  rows: T[];
  tableId: string;
};

const isInteractiveTableTarget = (target: EventTarget | null) =>
  target instanceof Element &&
  Boolean(
    target.closest(
      'a, button, input, select, textarea, tds-button, [role="button"]',
    ),
  );

export const TegelTable = <T,>({
  className,
  columns,
  compact = true,
  getRowKey,
  onRowClick,
  rowAriaLabel,
  rows,
  tableId,
}: TegelTableProps<T>) => (
  <div className={['tegel-data-table', className].filter(Boolean).join(' ')}>
    <TdsTable
      compactDesign={compact}
      noMinWidth
      responsive
      tableId={tableId}
      verticalDividers
      zebraMode="rows-even"
    >
      <TdsTableHeader>
        {columns.map(column => (
          <TdsHeaderCell
            key={column.key}
            cellKey={column.key}
            cellValue={column.header}
            customWidth={column.width}
            textAlign={column.align ?? 'left'}
          />
        ))}
      </TdsTableHeader>
      <TdsTableBody>
        {rows.map(row => (
          <TdsTableBodyRow
            key={getRowKey(row)}
            className={onRowClick ? 'tegel-data-table-clickable-row' : ''}
            role={onRowClick ? 'link' : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            aria-label={onRowClick ? rowAriaLabel?.(row) : undefined}
            onClick={event => {
              if (!onRowClick || isInteractiveTableTarget(event.target)) {
                return;
              }

              onRowClick(row);
            }}
            onKeyDown={event => {
              if (
                !onRowClick ||
                isInteractiveTableTarget(event.target) ||
                (event.key !== 'Enter' && event.key !== ' ')
              ) {
                return;
              }

              event.preventDefault();
              onRowClick(row);
            }}
          >
            {columns.map(column => (
              <TdsBodyCell
                key={column.key}
                cellKey={column.key}
                className={column.className}
                textAlign={column.align ?? 'left'}
              >
                {column.render(row)}
              </TdsBodyCell>
            ))}
          </TdsTableBodyRow>
        ))}
      </TdsTableBody>
    </TdsTable>
  </div>
);
